import React, { useCallback, useEffect, useState } from 'react';
import { Btn, C, Card } from '../../components/UI';
import { platformApi, PlatformIntegrationCredentialDto } from '../../services/api';

// ── Platform-managed OAuth app credentials ──────────────────────────────────────
// One shared Connected App/OAuth client per (provider, environment), set here instead of Key
// Vault/App Service settings. Set it once and every tenant, current and future, can connect
// immediately with no further engineering involvement. Sandbox and Production are separate slots
// — Intuit issues genuinely different Client ID/Secret pairs for each (Salesforce's Consumer Key
// happens to work for both, but the form always shows both slots for consistency). See
// PlatformIntegrationCredential's doc comment in Models.cs for the full rationale.

const PROVIDER_INFO: Record<string, { label: string; icon: string; help: string }> = {
    Salesforce: { label: 'Salesforce', icon: '☁️', help: 'Consumer Key and Consumer Secret from your Salesforce Connected App (Setup → App Manager). The same Consumer Key typically works for both Sandbox and Production — only the environment you connect against differs.' },
    QuickBooks: { label: 'QuickBooks Online', icon: '📊', help: 'From your Intuit Developer app’s Keys & OAuth tab. Development keys go in Sandbox; Production keys (issued once your app is approved) go in Production — these are genuinely different key pairs, not the same values.' },
};

function EnvironmentSlot({ cred, onSaved }: { cred: PlatformIntegrationCredentialDto; onSaved: () => void }) {
    const [editing, setEditing] = useState(false);
    const [clientId, setClientId] = useState(cred.clientId ?? '');
    const [clientSecret, setClientSecret] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const save = async () => {
        if (!clientId.trim() || !clientSecret.trim()) { setError('Both fields are required.'); return; }
        setSaving(true); setError('');
        try {
            await platformApi.setPlatformIntegration(cred.provider, cred.environment, clientId.trim(), clientSecret.trim());
            setEditing(false);
            setClientSecret('');
            onSaved();
        } catch (e: any) {
            setError(e.message || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: C.textSub }}>{cred.environment}</span>
                <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                    background: cred.clientId ? C.greenBg : '#F5F5F5',
                    color: cred.clientId ? C.greenMid : C.textMuted,
                }}>{cred.clientId ? 'Configured' : 'Not configured'}</span>
            </div>

            {!editing ? (
                <div>
                    {cred.clientId && (
                        <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 13 }}>
                            <div>
                                <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600 }}>Client ID</div>
                                <div style={{ color: C.text, fontFamily: 'monospace' }}>{cred.clientId}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600 }}>Secret</div>
                                <div style={{ color: C.text, fontFamily: 'monospace' }}>{cred.maskedClientSecret}</div>
                            </div>
                        </div>
                    )}
                    <Btn size="sm" variant="outline" onClick={() => setEditing(true)}>
                        {cred.clientId ? 'Update' : 'Set credentials'}
                    </Btn>
                </div>
            ) : (
                <div>
                    {error && <div style={{ color: '#DC2626', fontSize: 12, marginBottom: 10 }}>{error}</div>}
                    <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 4 }}>Client ID</label>
                        <input value={clientId} onChange={e => setClientId(e.target.value)}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 4 }}>Client Secret</label>
                        <input type="password" value={clientSecret} onChange={e => setClientSecret(e.target.value)}
                            placeholder={cred.clientId ? 'Enter a new secret to replace the current one' : ''}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Btn size="sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
                        <Btn size="sm" variant="outline" onClick={() => { setEditing(false); setError(''); setClientSecret(''); }}>Cancel</Btn>
                    </div>
                </div>
            )}
        </div>
    );
}

function ProviderCard({ provider, creds, onSaved }: { provider: string; creds: PlatformIntegrationCredentialDto[]; onSaved: () => void }) {
    const info = PROVIDER_INFO[provider] ?? { label: provider, icon: '🔌', help: '' };
    const sandbox = creds.find(c => c.environment === 'sandbox');
    const production = creds.find(c => c.environment === 'production');

    return (
        <Card style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <span style={{ fontSize: 20 }}>{info.icon}</span>
                <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{info.label}</div>
            </div>
            {info.help && <div style={{ fontSize: 12, color: C.textSub, marginBottom: 14 }}>{info.help}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {sandbox && <EnvironmentSlot cred={sandbox} onSaved={onSaved} />}
                {production && <EnvironmentSlot cred={production} onSaved={onSaved} />}
            </div>
        </Card>
    );
}

export default function IntegrationsPanel() {
    const [creds, setCreds] = useState<PlatformIntegrationCredentialDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try { setCreds(await platformApi.getPlatformIntegrations()); }
        catch (e: any) { setError(e.message || 'Failed to load integrations.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const providers = Array.from(new Set(creds.map(c => c.provider)));

    return (
        <div style={{ maxWidth: 760 }}>
            <div style={{ marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Integration Credentials</h2>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>
                    Shared OAuth app credentials, one Sandbox and one Production slot per provider. Every tenant authorizes against the same app and connects their own org — set this once and any tenant can connect immediately, no deploy needed.
                </div>
            </div>
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}
            {loading ? (
                <div style={{ color: C.textMuted, fontSize: 13 }}>Loading...</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {providers.map(provider => (
                        <ProviderCard key={provider} provider={provider} creds={creds.filter(c => c.provider === provider)} onSaved={load} />
                    ))}
                </div>
            )}
        </div>
    );
}
